import { nanoid } from '@reduxjs/toolkit'
import quote360TemplateCategoriesArray from 'src/data/360Template//categories.json'
import quote360TemplateSubCategoriesArray from 'src/data/360Template//subcategories.json'
import quoteAnimationAndLiveActionCategoriesArray from 'src/data/animation/quoteAnimationAndLiveAction/categories.json'
import quoteAnimationAndLiveActionSubCategoriesArray from 'src/data/animation/quoteAnimationAndLiveAction/subcategories.json'
import quoteAnimationOnlyCategoriesArray from 'src/data/animation/quoteAnimationOnly/categories.json'
import quoteAnimationOnlySubCategoriesArray from 'src/data/animation/quoteAnimationOnly/subcategories.json'
import cnc5chCategoriesArray from 'src/data/cnc/5ch/categories.json'
import cnc5chSubCategoriesArray from 'src/data/cnc/5ch/subcategories.json'
import quoteDigitalCategoriesArray from 'src/data/digital/categories.json'
import quoteDigitalSubCategoriesArray from 'src/data/digital/subcategories.json'
import categoriesArray from 'src/data/french/apfp/categories.json'
import subCategoriesArray from 'src/data/french/apfp/subcategories.json'
import postProductionCategoriesArray from 'src/data/french/postProduction/categories.json'
import photoGridSubCategoriesArray1_3 from 'src/data/photoGrid/1_3/subcategories.json'
import photoGridCategoriesArray from 'src/data/photoGrid/categories.json'
import photoGridSubCategoriesArray from 'src/data/photoGrid/subcategories.json'
import soundProdCategoriesArray from 'src/data/soundProd/categories.json'
import soundProdSubCategoriesArray from 'src/data/soundProd/subcategories.json'
import aicpCategoriesArray from 'src/data/usa/aicp/categories.json'
import aicpSubCategoriesArray from 'src/data/usa/aicp/subcategories.json'
import type { ReduxQuote, ReduxQuoteInfo } from 'src/types/apiType'
import type {
	Category,
	CategoryNewItem,
	SubCategory,
	SubCategoryNewItem,
} from 'src/types/jsonType'
import romanize from 'src/utils/romanize'

const categories = categoriesArray.reduce(
	(finalArray: Category[], item, index) => {
		const newItem = { ...item } as CategoryNewItem
		let intlCounterpart = null
		// Non internanlional categories are labelled like IA for (1 non international)
		const romanValue = romanize(index + 1)
		newItem.categoryPrefix = `${romanValue}A`
		newItem.isInternational = false
		newItem.supplierDefault = item.supplierDefault || false
		// If category has international counterpart label it like IB, IIB
		if (newItem.hasInternational) {
			intlCounterpart = {} as CategoryNewItem
			// add EXE to category names for international counterparts
			intlCounterpart.categoryUuid =
				newItem.categoryUuid_international as string
			intlCounterpart.category = `${newItem.category} - EXE`
			intlCounterpart.category_fr = `${newItem.category_fr} - EXE`
			intlCounterpart.categoryPrefix = `${romanValue}B`
			intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate =
				newItem.allowMarginAndOverheadPercentageGlobalUpdate
			intlCounterpart.isInternational = true
			intlCounterpart.supplierDefault = false
		}
		delete newItem.hasInternational
		newItem.categoryId = finalArray.length + 1
		// allowMarginAndOverheadPercentageGlobalUpdate is not required for categories so its removed
		if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
			delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
		}
		finalArray.push(newItem)
		if (intlCounterpart) {
			intlCounterpart.categoryId = finalArray.length + 1
			if (intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate) {
				delete intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate
			}
			finalArray.push(intlCounterpart)
		}
		return finalArray
	},
	[]
)

const postProductionCategories = postProductionCategoriesArray.reduce(
	(finalArray: Category[], item, index) => {
		const newItem = { ...item } as CategoryNewItem
		const intlCounterpart = null
		// Non internanlional categories are labelled like IA for (1 non international)
		const romanValue = romanize(index + 1)
		newItem.categoryPrefix = `${romanValue}A`
		newItem.isInternational = false
		newItem.supplierDefault = item.supplierDefault || false
		delete newItem.hasInternational
		newItem.categoryId = finalArray.length + 1
		// allowMarginAndOverheadPercentageGlobalUpdate is not required for categories so its removed
		if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
			delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
		}
		finalArray.push(newItem)
		return finalArray
	},
	[]
)

const soundProdCategories = soundProdCategoriesArray.reduce(
	(finalArray: Category[], item, index) => {
		const newItem = { ...item } as CategoryNewItem
		const intlCounterpart = null
		// Non internanlional categories are labelled like IA for (1 non international)
		const romanValue = romanize(index + 1)
		newItem.categoryPrefix = `${romanValue}A`
		newItem.isInternational = false
		// @ts-expect-error
		newItem.supplierDefault = item.supplierDefault || false
		delete newItem.hasInternational
		newItem.categoryId = finalArray.length + 1
		// allowMarginAndOverheadPercentageGlobalUpdate is not required for categories so its removed
		if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
			delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
		}
		finalArray.push(newItem)
		return finalArray
	},
	[]
)

const photoGridCategories = photoGridCategoriesArray.reduce(
	(finalArray: Category[], item, index) => {
		const newItem = { ...item } as CategoryNewItem
		const intlCounterpart = null
		// Non internanlional categories are labelled like IA for (1 non international)
		const romanValue = romanize(index + 1)
		newItem.categoryPrefix = `${romanValue}A`
		newItem.isInternational = false
		newItem.supplierDefault = false
		delete newItem.hasInternational
		newItem.categoryId = finalArray.length + 1
		// allowMarginAndOverheadPercentageGlobalUpdate is not required for categories so its removed
		if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
			delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
		}
		finalArray.push(newItem)
		return finalArray
	},
	[]
)

const aicpCategories = aicpCategoriesArray.reduce(
	(finalArray: Category[], item, index) => {
		const newItem = { ...item } as CategoryNewItem
		let intlCounterpart = null
		// Non internanlional categories are labelled like IA for (1 non international)
		const romanValue = romanize(index + 1)
		newItem.categoryPrefix = `${romanValue}A`
		newItem.isInternational = false
		// If category has international counterpart label it like IB, IIB
		if (newItem.hasInternational) {
			intlCounterpart = {} as CategoryNewItem
			// add EXE to category names for international counterparts
			intlCounterpart.categoryUuid =
				newItem.categoryUuid_international as string
			intlCounterpart.category = `${newItem.category} - EXE`
			intlCounterpart.category_fr = `${newItem.category_fr} - EXE`
			intlCounterpart.categoryPrefix = `${romanValue}B`
			intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate =
				newItem.allowMarginAndOverheadPercentageGlobalUpdate
			intlCounterpart.isInternational = true
		}
		delete newItem.hasInternational
		newItem.categoryId = finalArray.length + 1
		// allowMarginAndOverheadPercentageGlobalUpdate is not required for categories so its removed
		if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
			delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
		}
		finalArray.push(newItem)
		if (intlCounterpart) {
			intlCounterpart.categoryId = finalArray.length + 1
			if (intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate) {
				delete intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate
			}
			finalArray.push(intlCounterpart)
		}
		return finalArray
	},
	[]
)

//Category with labelValue in json and no international
export function getCategoryWithLabelValue(categoriesArray: any): Category[] {
	const categories = categoriesArray.reduce(
		(finalArray: Category[], item: any, index: number) => {
			const newItem = { ...item } as Category & {
				hasInternational?: boolean
				allowMarginAndOverheadPercentageGlobalUpdate?: boolean
			}

			// Non internanlional categories are labelled like IA for (1 non international)
			newItem.categoryPrefix = `${item.labelValue ?? ''}`
			newItem.isInternational = false
			// Deleting unwanted properties
			delete newItem.hasInternational
			newItem.categoryId = finalArray.length + 1
			// allowMarginAndOverheadPercentageGlobalUpdate is not required for categories so its removed
			if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
				delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
			}
			finalArray.push(newItem)
			return finalArray
		},
		[]
	)
	return categories
}

export function getCategoryWithLabelValueWithExe(
	categoriesArray: any
): Category[] {
	const categories = categoriesArray.reduce(
		(finalArray: Category[], item: any, index: number) => {
			const newItem = { ...item } as Category & {
				hasInternational?: boolean
				allowMarginAndOverheadPercentageGlobalUpdate?: boolean
			}
			let intlCounterpart = null

			const romanValue = romanize(index + 1)
			newItem.categoryPrefix = `${romanValue ?? ''}A`
			newItem.isInternational = false
			newItem.supplierDefault = item.supplierDefault || false
			if (newItem.hasInternational) {
				intlCounterpart = {} as CategoryNewItem
				// add EXE to category names for international counterparts
				intlCounterpart.categoryUuid =
					newItem.categoryUuid_international as string
				intlCounterpart.category = `${newItem.category} - EXE`
				intlCounterpart.category_fr = `${newItem.category_fr} - EXE`
				intlCounterpart.categoryPrefix = `${romanValue}B`
				intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate =
					newItem.allowMarginAndOverheadPercentageGlobalUpdate
				intlCounterpart.isInternational = true
				intlCounterpart.supplierDefault = false
			}
			// Deleting unwanted properties
			delete newItem.hasInternational
			newItem.categoryId = finalArray.length + 1
			// allowMarginAndOverheadPercentageGlobalUpdate is not required for categories so its removed
			if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
				delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
			}
			finalArray.push(newItem)

			if (intlCounterpart) {
				intlCounterpart.categoryId = finalArray.length + 1
				if (
					intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate
				) {
					delete intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate
				}
				finalArray.push(intlCounterpart)
			}
			return finalArray
		},
		[]
	)
	return categories
}

const cnc5chCategories = getCategoryWithLabelValue(cnc5chCategoriesArray)
const quoteAnimationOnlyCategories = getCategoryWithLabelValue(
	quoteAnimationOnlyCategoriesArray
)
const quoteAnimationAndLiveActionCategories = getCategoryWithLabelValue(
	quoteAnimationAndLiveActionCategoriesArray
)
const quoteDigitalCategories = getCategoryWithLabelValue(
	quoteDigitalCategoriesArray
)
const quote360TemplateCategories = getCategoryWithLabelValueWithExe(
	quote360TemplateCategoriesArray
)

const category = {
	category: 'Visual Effects, Design & Animation, Interactive',
	category_fr: 'Visual Effects, Design & Animation, Interactive',
	subCategory: 'Visual Effects, Design & Animation, Interactive',
	subCategory_fr: '10001',
	categoryUuid: 'b431c935-0fb9-4c3b-b075-b903654f025a',
	subCategoryUuid: 'b431c935-0fb9-4c3b-b075-b903654f025a',
	hasInternational: false,
	allowMarginAndOverheadPercentageGlobalUpdate: true,
}
const filteredAICPSubCategories = aicpSubCategoriesArray.filter(
	(c) => c.category !== 'Visual Effects, Design & Animation, Interactive'
)
const silcedAICPSubCategories = [
	...filteredAICPSubCategories.slice(0, 21),
	category,
	...filteredAICPSubCategories.slice(21),
]
const aicpSubCategories = silcedAICPSubCategories.reduce(
	(finalArray: SubCategory[], item, index) => {
		const newItem = { ...item } as SubCategoryNewItem
		let intlCounterpart = null
		const idValue = index + 1
		// Sub category for non international is labelled as 1A, 2A
		newItem.subCategoryPrefix = `${idValue}A`
		newItem.subCategorySuffix = 'A'
		newItem.isInternational = false
		// @ts-expect-error
		newItem.supplierDefault = item.supplierDefault || false
		// Set the parent category for france from above categories array
		newItem.category_fr = categories?.find(
			(c) => c?.category === newItem?.category
		)?.category_fr as string
		// For international they are labelled as 1B, 2B etc
		// Also needs to redo the parent category labelling for international
		if (newItem.hasInternational) {
			intlCounterpart = {} as SubCategoryNewItem
			intlCounterpart.subCategoryUuid = newItem.subCategoryUuid as string
			intlCounterpart.category = `${newItem.category} - EXE`
			intlCounterpart.category_fr = `${newItem.category_fr} - EXE`
			intlCounterpart.subCategory = newItem.subCategory
			intlCounterpart.subCategory_fr = newItem.subCategory_fr
			intlCounterpart.subCategoryPrefix = `${idValue}B`
			intlCounterpart.subCategorySuffix = 'B'
			intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate =
				newItem.allowMarginAndOverheadPercentageGlobalUpdate
			intlCounterpart.isInternational = true
			intlCounterpart.supplierDefault = false
		}
		delete newItem.hasInternational
		newItem.subCategoryId = finalArray.length + 1
		if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
			delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
		}
		finalArray.push(newItem)
		if (intlCounterpart) {
			intlCounterpart.subCategoryId = finalArray.length + 1
			if (intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate) {
				delete intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate
			}
			finalArray.push(intlCounterpart)
		}

		return finalArray
	},
	[]
)

function getSubCategoryWithLabelValue(subCategoriesArray: any): SubCategory[] {
	const subCategories = subCategoriesArray.reduce(
		(finalArray: SubCategory[], item: any) => {
			const newItem = { ...item } as SubCategoryNewItem
			let intlCounterpart = null
			// Sub category for non international is labelled as 1A, 2A
			newItem.subCategoryPrefix = `${item.labelValue ?? ''}`
			newItem.subCategorySuffix = 'A'
			newItem.isInternational = false
			// Set the parent category for france from above categories array
			newItem.category_fr = categories?.find(
				(c) => c?.category === newItem?.category
			)?.category_fr as string
			// For international they are labelled as 1B, 2B etc
			// Also needs to redo the parent category labelling for international
			if (newItem.hasInternational) {
				intlCounterpart = {} as SubCategoryNewItem
				intlCounterpart.subCategoryUuid =
					newItem.subCategoryUuid as string
				intlCounterpart.category = `${newItem.category} - EXE`
				intlCounterpart.category_fr = `${newItem.category_fr} - EXE`
				intlCounterpart.subCategory = newItem.subCategory
				intlCounterpart.subCategory_fr = newItem.subCategory_fr
				intlCounterpart.subCategoryPrefix = `${item.labelValue ?? ''}`
				intlCounterpart.subCategorySuffix = 'B'
				intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate =
					newItem.allowMarginAndOverheadPercentageGlobalUpdate
				intlCounterpart.isInternational = true
			}
			delete newItem.hasInternational
			newItem.subCategoryId = finalArray.length + 1
			if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
				delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
			}
			finalArray.push(newItem)
			if (intlCounterpart) {
				intlCounterpart.subCategoryId = finalArray.length + 1
				if (
					intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate
				) {
					delete intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate
				}
				finalArray.push(intlCounterpart)
			}

			return finalArray
		},
		[]
	)
	return subCategories
}

// Create an array for sub- categories from template in /data/subcategories
// Used to create the international counterparts for the sub-categories and labelling them
const subCategories = subCategoriesArray.reduce(
	(finalArray: SubCategory[], item, index) => {
		const newItem = { ...item } as SubCategoryNewItem
		let intlCounterpart = null
		const idValue = index + 1
		// Sub category for non international is labelled as 1A, 2A
		newItem.subCategoryPrefix = `${idValue}A`
		newItem.subCategorySuffix = 'A'
		newItem.isInternational = false
		// Set the parent category for france from above categories array
		newItem.category_fr = categories?.find(
			(c) => c?.category === newItem?.category
		)?.category_fr as string
		// For international they are labelled as 1B, 2B etc
		// Also needs to redo the parent category labelling for international
		if (newItem.hasInternational) {
			intlCounterpart = {} as SubCategoryNewItem
			intlCounterpart.category = `${newItem.category} - EXE`
			intlCounterpart.category_fr = `${newItem.category_fr} - EXE`
			intlCounterpart.subCategory = newItem.subCategory
			intlCounterpart.subCategory_fr = newItem.subCategory_fr
			intlCounterpart.subCategoryPrefix = `${idValue}B`
			intlCounterpart.subCategorySuffix = 'B'
			intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate =
				newItem.allowMarginAndOverheadPercentageGlobalUpdate
			intlCounterpart.isInternational = true
		}
		delete newItem.hasInternational
		newItem.subCategoryId = finalArray.length + 1
		if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
			delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
		}
		finalArray.push(newItem)
		if (intlCounterpart) {
			intlCounterpart.subCategoryId = finalArray.length + 1
			if (intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate) {
				delete intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate
			}
			finalArray.push(intlCounterpart)
		}
		return finalArray
	},
	[]
)

const cnc5chSubCategories = getSubCategoryWithLabelValue(
	cnc5chSubCategoriesArray
)
const quoteAnimationAndLiveActionSubCategories = getSubCategoryWithLabelValue(
	quoteAnimationAndLiveActionSubCategoriesArray
)
const quoteAnimationOnlySubCategories = getSubCategoryWithLabelValue(
	quoteAnimationOnlySubCategoriesArray
)
const quoteDigitalSubCategories = getSubCategoryWithLabelValue(
	quoteDigitalSubCategoriesArray
)

const quote360TemplateSubCategories = getSubCategoryWithLabelValue(
	quote360TemplateSubCategoriesArray
)
const getCategory = (categoryName: string, isInternational: boolean) => {
	const category = isInternational ? `${categoryName} - EXE` : categoryName
	// Returns the relevant category details for the categoryName and whether its international
	return categories.find(
		(c) => c.category === category && c.isInternational === isInternational
	)
}

const getAicpCategory = (categoryName: string) => {
	const category = categoryName
	// Returns the relevant category details for the categoryName and whether its international
	return aicpCategories.find((c) => c.category === category)
}

const getSubCategory = (
	categoryName: string,
	subCategoryName: string,
	isInternational: boolean
) => {
	const category = isInternational ? `${categoryName} - EXE` : categoryName
	// Returns the relevant subcategory details for the subcategoryName, same parent category and whether its international
	return (
		subCategories.find(
			(sc) =>
				sc.category === category &&
				sc.subCategory === subCategoryName &&
				sc.isInternational === isInternational
		) || {}
	)
}

const getAicpSubCategory = (categoryName: string, subCategoryName: string) => {
	const category = categoryName
	// Returns the relevant subcategory details for the subcategoryName, same parent category and whether its international
	return (
		subCategories.find(
			(sc) =>
				sc.category === category && sc.subCategory === subCategoryName
		) || {}
	)
}

const photoGridSubCategories = photoGridSubCategoriesArray.reduce(
	(finalArray: SubCategory[], item, index) => {
		const newItem = { ...item } as SubCategoryNewItem
		let intlCounterpart = null
		const idValue = index + 1
		// Sub category for non international is labelled as 1A, 2A
		newItem.subCategoryPrefix = `${idValue}A`
		newItem.subCategorySuffix = 'A'
		newItem.isInternational = false
		// Set the parent category for france from above categories array
		newItem.category_fr = photoGridCategories?.find(
			(c) => c?.category === newItem?.category
		)?.category_fr as string
		// For international they are labelled as 1B, 2B etc
		// Also needs to redo the parent category labelling for international
		if (newItem.hasInternational) {
			intlCounterpart = {} as SubCategoryNewItem
			intlCounterpart.subCategoryUuid = newItem.subCategoryUuid as string
			intlCounterpart.category = `${newItem.category} - EXE`
			intlCounterpart.category_fr = `${newItem.category_fr} - EXE`
			intlCounterpart.subCategory = newItem.subCategory
			intlCounterpart.subCategory_fr = newItem.subCategory_fr
			intlCounterpart.subCategoryPrefix = `${idValue}B`
			intlCounterpart.subCategorySuffix = 'B'
			intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate =
				newItem.allowMarginAndOverheadPercentageGlobalUpdate
			intlCounterpart.isInternational = true
		}
		delete newItem.hasInternational
		newItem.subCategoryId = finalArray.length + 1
		if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
			delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
		}
		finalArray.push(newItem)
		if (intlCounterpart) {
			intlCounterpart.subCategoryId = finalArray.length + 1
			if (intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate) {
				delete intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate
			}
			finalArray.push(intlCounterpart)
		}
		return finalArray
	},
	[]
)

const photoGridSubCategories1_3 = photoGridSubCategoriesArray1_3.reduce(
	(finalArray: SubCategory[], item, index) => {
		const newItem = { ...item } as SubCategoryNewItem
		let intlCounterpart = null
		const idValue = index + 1
		// Sub category for non international is labelled as 1A, 2A
		newItem.subCategoryPrefix = `${idValue}A`
		newItem.subCategorySuffix = 'A'
		newItem.isInternational = false
		// Set the parent category for france from above categories array
		newItem.category_fr = photoGridCategories?.find(
			(c) => c?.category === newItem?.category
		)?.category_fr as string
		// For international they are labelled as 1B, 2B etc
		// Also needs to redo the parent category labelling for international
		if (newItem.hasInternational) {
			intlCounterpart = {} as SubCategoryNewItem
			intlCounterpart.subCategoryUuid = newItem.subCategoryUuid as string
			intlCounterpart.category = `${newItem.category} - EXE`
			intlCounterpart.category_fr = `${newItem.category_fr} - EXE`
			intlCounterpart.subCategory = newItem.subCategory
			intlCounterpart.subCategory_fr = newItem.subCategory_fr
			intlCounterpart.subCategoryPrefix = `${idValue}B`
			intlCounterpart.subCategorySuffix = 'B'
			intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate =
				newItem.allowMarginAndOverheadPercentageGlobalUpdate
			intlCounterpart.isInternational = true
		}
		delete newItem.hasInternational
		newItem.subCategoryId = finalArray.length + 1
		if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
			delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
		}
		finalArray.push(newItem)
		if (intlCounterpart) {
			intlCounterpart.subCategoryId = finalArray.length + 1
			if (intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate) {
				delete intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate
			}
			finalArray.push(intlCounterpart)
		}
		return finalArray
	},
	[]
)

const soundProdSubCategories = soundProdSubCategoriesArray.reduce(
	(finalArray: SubCategory[], item, index) => {
		const newItem = { ...item } as SubCategoryNewItem
		let intlCounterpart = null
		const idValue = index + 1
		// Sub category for non international is labelled as 1A, 2A
		newItem.subCategoryPrefix = `${idValue}A`
		newItem.subCategorySuffix = 'A'
		newItem.isInternational = false
		// Set the parent category for france from above categories array
		newItem.category_fr = soundProdCategories?.find(
			(c) => c?.category === newItem?.category
		)?.category_fr as string
		// For international they are labelled as 1B, 2B etc
		// Also needs to redo the parent category labelling for international
		if (newItem.hasInternational) {
			intlCounterpart = {} as SubCategoryNewItem
			intlCounterpart.subCategoryUuid = newItem.subCategoryUuid as string
			intlCounterpart.category = `${newItem.category} - EXE`
			intlCounterpart.category_fr = `${newItem.category_fr} - EXE`
			intlCounterpart.subCategory = newItem.subCategory
			intlCounterpart.subCategory_fr = newItem.subCategory_fr
			intlCounterpart.subCategoryPrefix = `${idValue}B`
			intlCounterpart.subCategorySuffix = 'B'
			intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate =
				newItem.allowMarginAndOverheadPercentageGlobalUpdate
			intlCounterpart.isInternational = true
		}
		delete newItem.hasInternational
		newItem.subCategoryId = finalArray.length + 1
		if (newItem.allowMarginAndOverheadPercentageGlobalUpdate) {
			delete newItem.allowMarginAndOverheadPercentageGlobalUpdate
		}
		finalArray.push(newItem)
		if (intlCounterpart) {
			intlCounterpart.subCategoryId = finalArray.length + 1
			if (intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate) {
				delete intlCounterpart.allowMarginAndOverheadPercentageGlobalUpdate
			}
			finalArray.push(intlCounterpart)
		}
		return finalArray
	},
	[]
)

// Creates each individual costline that you see in the grid
function createCostLine(
	categoryName: string,
	subCategoryName: string,
	isInternational: boolean,
	otherAttributes = {} as any
) {
	// sets the base properties of the cost line
	const costLine: ReduxQuoteInfo = {
		allowMarginAndOverheadPercentageGlobalUpdate: true,
		...getCategory(categoryName, isInternational),
		...(getSubCategory(categoryName, subCategoryName, isInternational) ||
			null),
		id: nanoid(),
		quoteId: null,
		editable: true,
		costLineType: 'costLine',
		costLineLabel: null,
		totalHeading: null,
		language: 'en',
		item: null,
		item_fr: null,
		quantity: null,
		number: null,
		unit: null,
		rate: null,
		exchangeEnabled: false,
		exchangeRate: 1,
		rateInEuros: null,
		total: null,
		payrollTaxEnabled: 'No',
		payrollTaxType: null,
		agentCommissionPercentage: null,
		agentCommission: null,
		marginPercentage: null,
		margin: null,
		overheadPercentage: null,
		overhead: null,
		totalMargin: null,
		isInternational,
		isVisible: true,
		overtimeHoursCost: null,
		overtimeHours: null,
		overtimeDetails: [
			{
				multiplier: 1,
				overtimeHours: null,
			},
			{
				multiplier: 2,
				overtimeHours: null,
			},
			{
				multiplier: 3,
				overtimeHours: null,
			},
			{
				multiplier: 4,
				overtimeHours: null,
			},
		],
		...otherAttributes,
	}
	// sets the item name to total  if costline is of type categoryTotal or subCategoryTotal
	if (costLine.costLineType === 'subCategoryTotal') {
		costLine.item = `TOTAL - ${costLine.subCategoryPrefix}: ${costLine.subCategory}`
		costLine.item_fr = `TOTAL - ${costLine.subCategoryPrefix}: ${costLine.subCategory_fr}`
	}
	if (costLine.costLineType === 'categoryTotal') {
		costLine.item = `TOTAL - ${costLine.categoryPrefix}: ${costLine.category}`
		costLine.item_fr = `TOTAL - ${costLine.categoryPrefix}: ${costLine.category_fr}`
	}
	return costLine
}

function createNewCostline({
	previousCostlineAttributes,
}: {
	previousCostlineAttributes?: ReduxQuoteInfo
}) {
	const costLine = {
		allowMarginAndOverheadPercentageGlobalUpdate: true,
		id: nanoid(),
		costlineUuid: nanoid(),
		editable: true,
		costLineType: 'costLine',
		costLineLabel: null,
		totalHeading: null,
		language: 'en',
		item: null,
		item_fr: null,
		quantity: null,
		number: null,
		shootNumber: null,
		unit: null,
		rate: null,
		exchangeEnabled: false,
		exchangeRate: 1,
		rateInEuros: null,
		total: null,
		payrollTaxEnabled: 'No',
		payrollTaxType: null,
		agentCommissionPercentage: null,
		agentCommission: null,
		marginPercentage: null,
		margin: null,
		overheadPercentage: null,
		overhead: null,
		totalMargin: null,
		isVisible: true,
		overtimeHoursCost: null,
		overtimeHours: null,
		overtimeDetails: [
			{
				multiplier: 1,
				overtimeHours: null,
			},
			{
				multiplier: 2,
				overtimeHours: null,
			},
			{
				multiplier: 3,
				overtimeHours: null,
			},
			{
				multiplier: 4,
				overtimeHours: null,
			},
		],
		...previousCostlineAttributes,
	} as ReduxQuoteInfo
	return costLine
}

// * We use the totals to add new empty costlines to each subcategory
function addTotalCostLinesToCostLines(costLinesArray: ReduxQuoteInfo[]) {
	let currentCategoryId = costLinesArray[0].categoryId
	let currentSubCategoryId = costLinesArray[0].subCategoryId
	const costLinesArrayLastIndex = costLinesArray.length - 1
	const costLinesWithTotals = costLinesArray.reduce(
		(finalArray: ReduxQuoteInfo[], item, index, arr) => {
			let subCategoryTotalCostLine = null
			let categoryTotalCostLine = null
			const emptyCostlines = []

			const prevItem = arr[index - 1]
			let categoryName = ''
			//
			if (prevItem) {
				//
				categoryName = prevItem.category as string
				categoryName = categoryName.replace(' - EXE', '')
			}
			// If subcategory id changes we create a costline for total of the previous subcategory
			const subCategory = prevItem.subCategory as string
			const isInternational = prevItem.isInternational as boolean
			if (item.subCategoryId !== currentSubCategoryId) {
				subCategoryTotalCostLine = {
					...createCostLine(
						categoryName,
						subCategory,
						isInternational,
						{
							editable: false,
							costLineType: 'subCategoryTotal',
							payrollTaxEnabled: null,
							total: 0,
							totalMargin: 0,
							isInternational: prevItem.isInternational,
						}
					),
				}
				// Doesn't add a new empty costlines in these categories
				if (
					!['Insurance', 'Insurance - EXE'].includes(
						item.category as string
					)
				) {
					const otherAttributes = {
						quoteId: prevItem.quoteId,
						tenantId: prevItem.tenantId,
						freelancers: prevItem.freelancers,
						childFreelancers: prevItem.childFreelancers,
						activeFlag: true,
						item: 'Agent commission',
						item_fr: 'Commission agent',
						marginPercentage: 0,
						overheadPercentage: 0,
						totalMargin: 0,
						allowMarginAndOverheadPercentageGlobalUpdate: false,
					}
					emptyCostlines.push({
						...createCostLine(
							categoryName,
							subCategory,
							isInternational
						),
						...otherAttributes,
					})
					for (let step = 0; step < 3; step++) {
						emptyCostlines.push({
							...createCostLine(
								categoryName,
								subCategory,
								isInternational,
								{
									payrollTaxEnabled:
										prevItem.payrollTaxEnabled,
									payrollTaxType: prevItem.payrollTaxType,
									marginPercentage: prevItem.marginPercentage,
									overheadPercentage:
										prevItem.overheadPercentage,
									exchangeRate: prevItem.exchangeRate,
									exchangeEnabled: prevItem.exchangeEnabled,
									unit: prevItem.unit,
									isInternational: prevItem.isInternational,
								}
							),
						})
					}
				}

				currentSubCategoryId = item.subCategoryId
			}
			// If category id changes we create a costline for total of the previous category
			if (item.categoryId !== currentCategoryId) {
				categoryTotalCostLine = {
					...createCostLine(
						categoryName,
						subCategory,
						isInternational,
						{
							editable: false,
							costLineType: 'categoryTotal',
							payrollTaxEnabled: null,
							total: 0,
							totalMargin: 0,
							isInternational: prevItem.isInternational,
						}
					),
				}
				currentCategoryId = item.categoryId
			}
			// If categoryTotalCostLine/subCategoryTotalCostLine was created above we push that to the finalArray we are creating from reduce
			if (subCategoryTotalCostLine) {
				finalArray.concat(emptyCostlines)
				finalArray.push(subCategoryTotalCostLine)
			}
			if (categoryTotalCostLine) finalArray.push(categoryTotalCostLine)
			// if normal costline push the cosline to the finalArray
			finalArray.push(item)
			// reached end of the array
			// so add subcategory total and category total costlines for the last category & subcategory
			if (index === costLinesArrayLastIndex) {
				let categoryName = item.category as string
				const subCategory = item.subCategory as string
				const isInternational = item.isInternational as boolean

				categoryName = categoryName.replace(' - EXE', '')
				subCategoryTotalCostLine = {
					...createCostLine(
						categoryName,
						subCategory,
						isInternational,
						{
							editable: false,
							costLineType: 'subCategoryTotal',
							payrollTaxEnabled: null,
							total: 0,
							totalMargin: 0,
							isInternational: item.isInternational,
						}
					),
				}
				categoryTotalCostLine = {
					...createCostLine(
						categoryName,
						subCategory,
						isInternational,
						{
							editable: false,
							costLineType: 'categoryTotal',
							payrollTaxEnabled: null,
							total: 0,
							totalMargin: 0,
							isInternational: item.isInternational,
						}
					),
				}
				finalArray.push(subCategoryTotalCostLine)
				finalArray.push(categoryTotalCostLine)
			}
			return finalArray
		},
		[]
	)
	return costLinesWithTotals
}

// For the left handside most column on react-data-grid
function createCostLineIdsAndLabels(costLines: ReduxQuoteInfo[]) {
	let costLineLabelIndex = 100
	const costLineLabelSuffix = 'A'
	const costLineLabelSuffixInternational = 'B'
	return costLines.map((c, i) => {
		c.tableRowIndex = i
		if (c.costLineType === 'costLine') {
			c.costLineLabel = c.isInternational
				? `${costLineLabelIndex}${costLineLabelSuffixInternational}`
				: `${costLineLabelIndex}${costLineLabelSuffix}`
			costLineLabelIndex++
		}
		return c
	})
}

interface AdditionalAttributes extends ReduxQuote {
	quoteId: string
}

export {
	aicpCategories,
	aicpSubCategories,
	categories,
	cnc5chCategories,
	cnc5chSubCategories,
	createCostLine,
	createCostLineIdsAndLabels,
	createNewCostline,
	photoGridCategories,
	photoGridSubCategories,
	photoGridSubCategories1_3,
	postProductionCategories,
	quote360TemplateCategories,
	quote360TemplateSubCategories,
	quoteAnimationAndLiveActionCategories,
	quoteAnimationAndLiveActionSubCategories,
	quoteAnimationOnlyCategories,
	quoteAnimationOnlySubCategories,
	quoteDigitalCategories,
	quoteDigitalSubCategories,
	soundProdCategories,
	soundProdSubCategories,
	subCategories,
}
